<template>
	<div ref="element" class="transition-height" :class="{ 'transition-height-active': isTweening }">
		<transition
			:duration="duration"
			:name="name"
			@before-leave="beforeLeave"
			@leave="leave"
			@after-leave="afterLeave"
			@before-enter="beforeEnter"
			@enter="enter"
			@after-enter="afterEnter"
		>
			<slot />
		</transition>
	</div>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue'

// Wait a frame
var defer = (cb: Function) => setTimeout(cb, 50)

// Refs
const element = ref<HTMLElement | null>(null)

// Props
const props = defineProps({
	// The name of Vue transition that should be used
	name: {
		type: String,
		required: true
	},

	// Same as Vue transition `mode` property
	mode: {
		type: String,
		default: '',
		validator(val: string) { return ['out-in', ''].includes(val); }
	},

	// Do a switch style tween, rather than a toggle (the default)
	switching: Boolean,

	// Same as Vue transition `duration` property
	duration: {
		type: Number,
		default: null
	}
})

// Emits
const emit = defineEmits(['beforeLeave', 'beforeEnter', 'leave', 'enter', 'afterLeave', 'afterEnter'])

// Reactives
const isTweening = ref(false)

// Methods
// Capture the intial height when switching or the starting height when
// toggling close
function beforeLeave(el: Element){
	if(element.value) element.value.style.height = outerHeight(el) + 'px'
	isTweening.value = true

	return emit('beforeLeave', el)
}

// When toggling, set the initial height to 0
async function beforeEnter(el: Element){
	await nextTick()

	isTweening.value = true

	if(!props.switching && element.value) {
		element.value.style.height = "0"
	}

	if(element.value) element.value.style.height = outerHeight(el) + 'px'

	return emit('beforeEnter', el)
}

// When leaving, if toggling, set the height to 0 after a tick
async function leave(el: Element){
	await nextTick()

	if(!props.switching && element.value) {
		defer(() => element.value?.style?.height ? element.value.style.height = "0" : null)
	}

	return emit('leave', el)
}

/**
 * When entering, always set the new height after a tick.
 * @param el 
 */
function enter(el: Element){
	defer(() => element.value?.style?.height ? element.value.style.height = outerHeight(el) + 'px' : null)

	return emit('enter', el);
}

// Reset the state unless we're doing an out-in transition, in which case
// this should get triggered on enter.
function afterLeave(el: Element){
	if(props.mode !== 'out-in') {
		reset()
	}

	return emit('afterLeave', el);
}

// Clear the height after enter finishes in all cases. This is valid
// because either it's a toggle animation and this won't get called at all
// when v-if="false" or we're switching and it fires at the end in either
// supported case.
function afterEnter(el: Element){
	reset()

	return emit('afterEnter', el);
}

// Reset the state
function reset(){
	if(element.value) element.value.style.height = ''
	return isTweening.value = false
}

/**
 * Returns the element height including margins
 * @param element - element
 * @returns {number}
 */
function outerHeight(element: Element): number {
	const el = element as HTMLElement
	var styles = window.getComputedStyle(el)
	
	// Save size of element borders to restore later
	var borderTop = styles.getPropertyValue("border-top")
	if(parseInt(borderTop) == 0){ // We have no border on top so let's add a temporary one
		el.style.borderTop = "1px solid transparent"
	}

	var borderBottom = styles.getPropertyValue("border-bottom")
	if(parseInt(borderBottom) == 0){ // We have no border on top so let's add a temporary one
		el.style.borderBottom = "1px solid transparent"
	}

	var height = parseInt(window.getComputedStyle(el).blockSize)

	// Let restore everything back
	el.style.borderTop = borderTop
	el.style.borderBottom = borderBottom

	return height
}
</script>